"use client";
import React from "react";
import { Box, Typography, Container, Paper, Chip } from "@mui/material";
import Image from "next/image";
import { IMAGES } from "../constants/images";
import { useTranslation } from "react-i18next";

export default function RecruitmentIssues() {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const problemsRaw = t("recruitmentIssues.problems", { returnObjects: true });
  const problems = Array.isArray(problemsRaw) ? problemsRaw : [];

  return (
    <Box
      sx={{
        backgroundColor: "#ffffff",
        px: { xs: 2, sm: 3, md: 4, lg: 0 },
        pt: { xs: -10, sm: 6, lg: 8 },
        mt: { xs: -7, sm: 6, lg: 0 },
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: { xs: 4, lg: 8 },
            alignItems: "flex-start",
          }}
        >
          {/* Left Side */}
          <Box
            sx={{
              flex: { xs: "1", lg: isArabic ? "0 0 49%" : "0 0 47%" },
              position: "relative",
            }}
          >
            {" "}
            <Chip
              label={t("recruitmentIssues.chip")}
              sx={{
                backgroundColor: "white",
                border: "1px solid #0206174D",
                mb: { xs: 1.5, md: 2 },
                color: "#9C9FAD",
                fontSize: { xs: "12px", sm: "14px", md: "16px" },
                fontWeight: "500",
                textTransform: "capitalize",
                px: { xs: 0.5, sm: 0.7 },
                py: { xs: 1.2, sm: 1.5, md: 2.3 },
                fontFamily: "var(--font-outfit)",
                borderRadius: "40px",
              }}
            />
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontWeight: 600,
                fontSize: {
                  xs: "1.8rem",
                  sm: "2.2rem",
                  md: "2.8rem",
                  lg: "2.8rem",
                  xl: "3.2rem",
                },
                lineHeight: { xs: 1.2, sm: 1.25, md: isArabic ? 1.3 : 1.1 },
                mb: 3,
                maxWidth: { xs: "100%", sm: "95%", md: "100%" },
                fontFamily: "var(--font-plusjakarta)",
                textAlign: isArabic ? "right" : "left",
              }}
            >
              {t("recruitmentIssues.heading1")}{" "}
              <Box
                component="span"
                sx={{ color: "#ef4444", display: "inline" }}
              >
                {t("recruitmentIssues.headingHighlight1")}
              </Box>{" "}
              {t("recruitmentIssues.heading2")}{" "}
              <Box component="span" sx={{ color: "#ef4444" }}>
                {t("recruitmentIssues.headingHighlight2")}
              </Box>{" "}
              {t("recruitmentIssues.heading3")}
            </Typography>
            {/* Left Image */}
            <Box
              sx={{
                position: "relative",
                width: "100%",
                maxWidth: { xs: "100%", sm: 500, md: 600 },
                mt: "-11%",
                display: { xs: "none", sm: "none", md: "block" },
              }}
            >
              <Image
                src={
                  isArabic
                    ? IMAGES.RECRUITMENT_ISSUES_LEFT_IMG_ARABIC
                    : IMAGES.RECRUITMENT_ISSUES_LEFT_IMG
                }
                alt="Common hiring problems solved by AI recruiter and AI recruiting software"
                width={600}
                height={400}
                style={{
                  width: "112%",
                  height: "auto",
                  borderRadius: "16px",
                  objectFit: "contain",
                }}
                priority
              />
            </Box>
          </Box>

          {/* Right Side - Problems */}
          <Box
            sx={{
              flex: { xs: "1", lg: "0 0 55%" },
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
              mt: { sm: 0, lg: "8%" },
              ml: isArabic ? 0 : { sm: 0, lg: "3%", xl: "10%" },
              mr: isArabic ? { sm: 0, lg: "3%", xl: "10%" } : 0,
            }}
          >
            {problems.map((problem, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexDirection: isArabic ? "row" : "row",
                  gap: 2,
                  p: 3,
                  backgroundColor: "#F6F6F6",
                  width: { xs: "100%", sm: "90%", md: "80%" },
                  borderRadius: "12px",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#F6F6F6",
                    transform: isArabic
                      ? "translateX(-4px)"
                      : "translateX(4px)",
                  },
                }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 48,
                    height: 48,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Image
                    src={IMAGES.PROBLEM_ICON}
                    alt="hiring problem icon"
                    width={40}
                    height={40}
                    style={{
                      width: "40px",
                      height: "40px",
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: { xs: "0.95rem", sm: "1rem", md: "1.1rem" },
                    color: "#231A30",
                    fontWeight: 500,
                    fontFamily: "var(--font-outfit)",
                    textAlign: isArabic ? "right" : "left",
                  }}
                >
                  {problem}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
